import { format } from "date-fns";

import { DigitalVisitReasonEnum } from "@/lib/__generated/api";
import { DataLayerStepNames } from "@/lib/constants";
import { getBaseDataLayerParams } from "@/lib/utils/dataLayerParams";

import { schedulerSetup } from "../schedulerSetup";
import { getPatientAge } from "../utils/patient-age.util";
import { buildSlotsSearchInput, getSlotsSearchStartDate } from "../utils/slotsSearchInput.util";

export const dateTimeSelectionState = schedulerSetup.createStateConfig({
  meta: {
    componentKey: "date-time-selection",
    label: "dateAndTime",
    // Slot clicks advance the wizard on their own, so this step has no footer, and its Back button
    // lives inside it. Back-navigation is the sidebar's job here.
    showFooter: false,
    getSubtitle: (ctx) => {
      const { date, time } = ctx.appointmentData ?? {};
      if (!date || !time) return undefined;

      const formattedDate = new Date(`${date}T00:00:00`).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });

      return `${formattedDate} - ${time}`;
    },
  },
  initial: "waitingForFacility",
  entry: { type: "initSlotsSearchDate" },
  exit: { type: "stopNearbySlots" },
  on: {
    // No BACK handler: the step renders without a footer (see `meta` above), so there is
    // no Back button to send it. Back-navigation happens through the sidebar's GOTO_STEP instead.
    CLEAR_APPOINTMENT: { actions: { type: "clearAppointmentData" } },
    NEARBY_SLOTS_LOADED: {
      actions: {
        type: "assignNearbySlotsData",
        params: ({ event }) => ({
          nearbySlots: event.nearbySlots,
        }),
      },
    },
    NEARBY_SLOTS_FAILED: {
      actions: { type: "clearNearbySlotsData" },
    },
    REFRESH_SLOTS: {
      target: ".waitingForFacility",
      // Re-picking the date already in play would otherwise wipe the grid for an identical fetch.
      guard: ({ context, event }) => (event.startDate ?? null) !== context.slotsSearchDate,
      actions: {
        type: "updateSlotsSearchDate",
        params: ({ event }) => ({ startDate: event.startDate }),
      },
    },
    SYNC_FACILITY: {
      target: ".waitingForFacility",
      actions: [
        {
          type: "assignFacilityAndStateCode",
          params: ({ event }) => ({ facility: event.facility }),
        },
        { type: "resetSlotsData" },
      ],
    },
  },
  states: {
    waitingForFacility: {
      tags: ["loading"],
      always: { target: "resolvingFacility", guard: "hasFacilityCode" },
    },
    resolvingFacility: {
      tags: ["loading"],
      invoke: {
        src: "getFacilityLocations",
        input: ({ context }) => ({
          digitalVisitReason: context.reasonForVisit ?? "",
          longitude: context.facility?.location?.longitude ?? 0,
          latitude: context.facility?.location?.latitude ?? 0,
          // Only sent when derivable — the route strips falsy patientAge, so undefined
          // (Motto/Dentures URL flow, DOB not yet entered) is safe here.
          patientAge: getPatientAge(context.patientData.dateOfBirth),
          application: "AspenDental",
        }),
        onDone: [
          {
            guard: ({ context, event }) => {
              // getFacilityLocations is not day-of-service aware, so on that flow it suggests the
              // nearest ordinary office. Swapping to it would be reverted by the filtered facility
              // endpoint back to the office already selected, and the unchanged code means no
              // SYNC_FACILITY comes back — the step would sit in swappingFacility until the 5s
              // timeout. The resolver already picked the nearest participating office; trust it.
              if (context.reasonForVisit === DigitalVisitReasonEnum.HealthyMouthMovement)
                return false;
              const suggested = event.output?.[0]?.facilityCode;
              return !!suggested && suggested !== context.facility?.code;
            },
            target: "swappingFacility",
            actions: {
              type: "emitFacilitySwap",
              params: ({ event }) => ({
                code: event.output[0].facilityCode,
              }),
            },
          },
          { target: "loadingSlots" },
        ],
        onError: { target: "loadingSlots" },
      },
    },
    swappingFacility: {
      tags: ["loading"],
      after: { 5000: { target: "loadingSlots" } },
    },
    loadingSlots: {
      tags: ["loading"],
      // Nearby availability is spawned rather than invoked so it starts with the primary request
      // but survives the move to `idle` — a slow nearby response never holds back the slot grid.
      entry: { type: "spawnNearbySlots" },
      invoke: {
        src: "fetchSlots",
        input: buildSlotsSearchInput,
        onDone: [
          {
            target: "idle",
            guard: ({ event }) => event.output.length === 0,
            actions: [
              {
                type: "assignSlotsData",
                params: ({ event }) => ({ slots: event.output }),
              },
              {
                type: "dataLayerPush",
                params: ({ context }) => ({
                  event: "no_appointments_available_msg",
                  scheduling_filter_used: false,
                  step_name: DataLayerStepNames.timeSelection,
                  ...getBaseDataLayerParams(context),
                  calendar_selected_date: format(getSlotsSearchStartDate(context), "MM/dd/yyyy"),
                }),
              },
            ],
          },
          {
            target: "idle",
            actions: {
              type: "assignSlotsData",
              params: ({ event }) => ({ slots: event.output }),
            },
          },
        ],
        onError: {
          target: "slotsError",
          actions: [
            { type: "stopNearbySlots" },
            {
              type: "assignSlotsError",
              params: ({ event }) => ({
                error: event.error instanceof Error ? event.error.message : "Failed to fetch slots",
              }),
            },
          ],
        },
      },
    },
    idle: {
      on: {
        SELECT_APPOINTMENT: {
          target: "complete",
          actions: [
            {
              type: "assignAppointmentData",
              params: ({ event }) => ({
                appointmentData: event.appointmentData,
              }),
            },
            { type: "markStepComplete", params: { step: "dateTimeSelection" } },
            {
              type: "trackUserEvent",
              params: ({ context }) => ({
                eventType: "available_appointments_returned",
                eventData: {
                  facility_code: context.facility?.code,
                },
              }),
            },
          ],
        },
      },
    },
    slotsError: {
      tags: ["slotsError"],
      on: { RETRY_SLOTS: "waitingForFacility" },
    },
    complete: { type: "final" },
  },
  onDone: [
    { guard: "isSlotFirstFlow", target: "patientInformation" },
    { guard: "isPatientUnder18", target: "guarantorInformation" },
    { target: "appointmentReview" },
  ],
} as const);
